import type { ServerFrame } from './transport.ts';

/**
 * The three objects this drives, as the three calls it makes on them.
 *
 * Read through the caller's refs rather than held directly: `SessionView`
 * builds the bridge, the transport and the playback queue in one tick and
 * replaces none of them, but taking them as values would fix a construction
 * order between four objects that otherwise have none.
 */
export interface BargeInPorts {
  /** `AudioBridge.setTutorSpeaking`. */
  tellWorklet(on: boolean): void;
  /** `AudioTransport.cancel`. */
  cancel(playedThrough: number | null): void;
  /** `PlaybackQueue.lastPlayedSentence`. */
  lastPlayed(): number | null;
  /** `PlaybackQueue.flush`. */
  flush(): void;
  /** `PlaybackQueue.stopReplay`. */
  stopReplay(): void;
}

/**
 * The main thread's half of barge-in -- spec §7.
 *
 * Two jobs, and they share one piece of state. It tells the worklet when the
 * tutor is speaking, which is what arms `Endpointer`'s sustained-speech rule;
 * and when hands-free opens an utterance anyway, it sends the `CANCEL` that
 * stops the reply and tells the server how much of it the learner actually
 * heard.
 *
 * Its own module rather than a few lines in `SessionView.tsx`, because the
 * invariant below is the whole of the risk here and a `.tsx` is not reachable
 * from `node --test`.
 *
 * **Every path that sets the flag true has a matching clear.** A flag left on
 * fails silently: nothing breaks, no reply is lost, and every later hands-free
 * onset is simply held back by up to `BARGE_IN_SUSTAIN_MS` for the rest of the
 * session with nothing on screen to explain it. So the clears are enumerated
 * here rather than left to fall out --
 *
 * * [`playbackDrained`], the tutor's audio actually stopping -- which the
 *   orchestrator's `TTS_DONE` does not mean and is sent seconds before, see
 *   `PlaybackQueue.armSilence`. It reaches here on every way out of a turn,
 *   including one that failed and one that synthesised nothing, because the
 *   queue settles an armed terminator with no audio behind it at once;
 * * the barge-in itself, in [`captureStarted`], which must not wait a round
 *   trip for the cancelled turn's terminator, and which silences the reply so
 *   that clearing it there is true rather than merely early;
 * * [`stop`], for the endings after which no frame arrives at all -- a socket
 *   that dropped, and a session the learner ended.
 *
 * A replay (hear-again design §4.2) sets the flag too, and has its own clears:
 * [`replayEnded`] when it plays out or is stopped, [`captureStarted`] when the
 * learner speaks over it, a reply's first chunk, which takes the flag over as
 * a reply's, and [`stop`].
 */
export class BargeIn {
  private speaking = false;
  /**
   * What is audible while `speaking`. A replay is no turn the server is
   * running, so a capture over it must never send the `CANCEL` a reply gets.
   */
  private source: 'reply' | 'replay' = 'reply';
  private readonly ports: BargeInPorts;

  /**
   * A plain field, not a constructor parameter property: `node --test` strips
   * types without transforming them, and a parameter property is syntax it
   * refuses outright.
   */
  constructor(ports: BargeInPorts) {
    this.ports = ports;
  }

  /**
   * The tutor becomes audible at the first chunk of a reply. It stops being
   * audible at [`playbackDrained`], not at any frame: `TTS_DONE` says the
   * server has finished *sending*, and the client has seconds of that reply
   * still scheduled when it arrives.
   *
   * `sentenceEnd` is not a boundary either: it ends one sentence of a reply
   * that is still speaking.
   */
  onFrame(frame: ServerFrame): void {
    if (frame.kind !== 'chunk') return;
    this.source = 'reply';
    this.set(true);
  }

  /**
   * `PlaybackQueue` has run out of scheduled audio for a finished turn. A
   * reply's end only: a turn that synthesised nothing settles at once, and may
   * do so while a replay it did not know about is audible.
   */
  playbackDrained(): void {
    if (this.source === 'reply') this.set(false);
  }

  replayStarted(): void {
    this.source = 'replay';
    this.set(true);
  }

  /** Only a replay's own end: one that lands after a reply took over is late. */
  replayEnded(): void {
    if (this.source === 'replay') this.set(false);
  }

  /**
   * An utterance opened. If the tutor was speaking, that is barge-in. From
   * hands-free, `Endpointer` has already held this onset for
   * `BARGE_IN_SUSTAIN_MS` of speech, so it is the learner and not an echo; a
   * press of the button needs no such proof.
   *
   * Nothing is sent when the tutor is silent, and that is not merely tidiness.
   * A `CANCEL` reaching the server with no turn running discards whatever the
   * socket has accumulated, so an unconditional one here would throw away the
   * front of every ordinary hands-free utterance.
   *
   * A manual press comes through here too, from `SessionProvider`'s
   * `beginUtterance`: a learner holding the button to answer over a reply
   * they had heard enough of got the rest of it played into their answer,
   * and the tutor turn stored as if they had heard it all.
   *
   * The three calls are ordered, and the order is the argument for all of
   * them. `lastPlayed` is read first, because the flush is what makes that
   * index true -- it is "the last sentence the learner heard" only once
   * nothing after it can sound. The flush is second, because it is what stops
   * the tutor. Clearing the flag is last and is only correct *because* of the
   * flush: `PlaybackQueue` schedules the whole reply ahead on one timeline, so
   * without it the tutor stays audible for seconds after the cancel, and a
   * flag cleared here would switch the sustained-speech guard off for exactly
   * those seconds -- leaving the tutor's own tail free to open the next
   * utterance and then to pad it, with browser AEC the only thing in the way.
   */
  captureStarted(): void {
    if (!this.speaking) return;
    // No `CANCEL` and no flush for a replay (§2): a `CANCEL` with no turn
    // running discards the very utterance this press is starting.
    if (this.source === 'replay') {
      this.ports.stopReplay();
      this.set(false);
      return;
    }
    this.ports.cancel(this.ports.lastPlayed());
    this.ports.flush();
    this.set(false);
  }

  /** The session ended, or the socket did: no terminator is coming. */
  stop(): void {
    this.set(false);
  }

  /**
   * Posted only on a change. A reply is hundreds of chunks and each one would
   * otherwise be a port message saying what the last one said.
   */
  private set(on: boolean): void {
    if (this.speaking === on) return;
    this.speaking = on;
    this.ports.tellWorklet(on);
  }
}
